import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import Swal from "sweetalert2";
import DommusSwitchControl from "../../../../../components/DommusSwitchControl";
import { trackPromise } from "react-promise-tracker";
import { errorToast, successToast } from "../../../../../components/DommusToast";
import { MeioComunicacaoContext } from "../../../../../contexts/MeioComunicacaoContext";
import { TemplateContext } from "../../../../../contexts/TemplateContext";
import caAPI from "../../../../../services/caAPI";
import "./index.scss";
import { EmpreendimentoContext } from "../../../../../contexts/EmpreendimentosContext";

export function FormConfiguracaoContato({closeModal, listaAgentesAtivos}) {
  const {contatoOrigemSelecionado, meiosComunicacao, setMeiosComunicacao, setContatoOrigemSelecionado} = useContext(MeioComunicacaoContext);
  const { empreendimentoLista } = useContext(EmpreendimentoContext)
  const { templates } = useContext(TemplateContext);
  const { control, handleSubmit, register, setValue } = useForm({
    defaultValues: {
      account_sid_twilio: contatoOrigemSelecionado?.account_sid_twilio ?? "",
      id_agente_ia: contatoOrigemSelecionado?.id_agente_ia ?? null,
      id_empreendimento: contatoOrigemSelecionado?.id_empreendimento ?? null,
      criar_atendimento_whatsapp_auto: contatoOrigemSelecionado?.criar_atendimento_whatsapp_auto ?? false,
      id_contato_auto_criar_atendimento: contatoOrigemSelecionado?.id_contato_auto_criar_atendimento ?? "",
      id_template_criar_atendimento_auto: contatoOrigemSelecionado?.id_template_criar_atendimento_auto ?? "",
      id_template_resposta_auto_criar_atendimento: contatoOrigemSelecionado?.id_template_resposta_auto_criar_atendimento ?? "",
    },
  });
  useEffect(() => {
    setValue('id_agente_ia', contatoOrigemSelecionado?.id_agente_ia ?? null);
  }, [contatoOrigemSelecionado, setValue]);

  const meioAtual = useMemo(() => (meiosComunicacao.find((meio) => Number(meio.id) === Number(contatoOrigemSelecionado?.id_meio_comunicacao))), [meiosComunicacao, contatoOrigemSelecionado]);
  const contatosOrigem = useMemo(() => (meioAtual?.contatos_origem ?? []), [meioAtual]);

  const opcoesContatosOrigem = useMemo(
    () => contatosOrigem.map((c) => ({ value: c.id_contato_origem, label: c.descricao || c.valor })),
    [contatosOrigem]
  );

  const opcoesAgentesIa = useMemo(() => {
    const idConfirmado = contatoOrigemSelecionado?.id_agente_ia ?? null;
    const agenteAtual = listaAgentesAtivos.find((agente) => Number(agente.id_agente_ia) === Number(idConfirmado));
    const agenteInativo = idConfirmado !== null && !agenteAtual
      ? [{
          value: idConfirmado,
          label: `${contatoOrigemSelecionado?.agente_ia?.nome || 'Agente de IA'} (inativo)`,
          isDisabled: true,
        }]
      : [];

    return [
      { value: null, label: 'Nenhum' },
      ...agenteInativo,
      ...listaAgentesAtivos.map((agente) => ({ value: agente.id_agente_ia, label: agente.nome })),
    ];
  }, [contatoOrigemSelecionado, listaAgentesAtivos]);

  const opcoesTemplates = useMemo(
    () => templates.map((t) => ({ value: t.id, label: t.nome })),
    [templates]
  );

  const opcoesEmpreendimento = useMemo(()=>{
    return empreendimentoLista.map((e) => ({ value: e.id, label: e.nome }))
  },[empreendimentoLista])

  const atualizarContatoOrigem = useCallback((contatoAtualizado) => {
    setMeiosComunicacao((current) => current.map((meioComunicacao) => {
      if (Number(meioComunicacao.id) !== Number(contatoOrigemSelecionado?.id_meio_comunicacao)) return meioComunicacao;

      return {
        ...meioComunicacao,
        contatos_origem: meioComunicacao.contatos_origem.map((contatoOrigem) => {
          if (Number(contatoOrigem.id_contato_origem ?? contatoOrigem?.id) !== Number(contatoOrigemSelecionado.id_contato_origem)) return contatoOrigem;
          return { ...contatoOrigem, ...contatoAtualizado };
        }),
      };
    }));
    setContatoOrigemSelecionado((contatoAtual) => ({ ...contatoAtual, ...contatoAtualizado }));
  }, [contatoOrigemSelecionado, setContatoOrigemSelecionado, setMeiosComunicacao]);

  const handleAlterarAgenteIa = useCallback((idAgenteIa, field) => {
    const idConfirmado = contatoOrigemSelecionado?.id_agente_ia ?? null;
    const mesmoAgente = idAgenteIa === null
      ? idConfirmado === null
      : Number(idAgenteIa) === Number(idConfirmado);
    if (mesmoAgente) return;

    Swal.fire({
      title: 'Confirmar alteração?',
      text: idAgenteIa === null
        ? 'Deseja desvincular o Agente de IA deste Ramal?'
        : 'Deseja vincular este Agente de IA ao Ramal?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (!result.isConfirmed) {
        field.onChange(idConfirmado);
        return;
      }

      let requisicao;
      try {
        requisicao = caAPI.contato_origem.atualizarAgenteIa(contatoOrigemSelecionado.id_contato_origem, idAgenteIa);
      } catch (erro) {
        field.onChange(idConfirmado);
        Swal.fire({ titleText: 'Erro', text: 'Erro inesperado ao atualizar Agente de IA.', icon: 'error' });
        return;
      }

      trackPromise(
        requisicao
          .then((response) => {
            const contatoAtualizado = response.data?.data ?? response.data;
            const novoIdAgenteIa = contatoAtualizado.id_agente_ia ?? null;
            atualizarContatoOrigem(contatoAtualizado);
            field.onChange(novoIdAgenteIa);
            successToast.fire({ text: 'Agente de IA atualizado com sucesso.' });
          })
          .catch((erro) => {
            field.onChange(idConfirmado);
            Swal.fire({
              titleText: 'Erro',
              text: caAPI.formatServerError(erro).message,
              icon: 'error',
            });
          })
      );
    });
  }, [atualizarContatoOrigem, contatoOrigemSelecionado]);

  const handleSalvar = (dados) => {
    dados.criar_atendimento_whatsapp_auto = dados?.criar_atendimento_whatsapp_auto ? '1' : '0';
    trackPromise(
      caAPI.contato_origem
        .update(contatoOrigemSelecionado.id_contato_origem, dados)
        .then((response) => {
          successToast.fire({ text: "Configurações salvas com sucesso!" });
          atualizarContatoOrigem(response.data);
        })
        .catch(() => {
          errorToast.fire({ text: "Houve um erro ao salvar as configurações." });
        })
    );
  };

  return (
    <Form className="form-configuracao-contato-auto" onSubmit={handleSubmit(handleSalvar)}>
      {meioAtual?.slug === 'whatsapp' && (
      <Form.Group className="form-configuracao-contato-auto__grupo">
        <Form.Label>Agente de IA</Form.Label>
        <Controller
          name="id_agente_ia"
          control={control}
          render={({ field }) => (
            <Select
              options={opcoesAgentesIa}
              value={opcoesAgentesIa.find((opcao) => (
                opcao.value === null
                  ? field.value === null
                  : Number(opcao.value) === Number(field.value)
              )) || null}
              onChange={(opcao) => handleAlterarAgenteIa(opcao?.value ?? null, field)}
              isOptionDisabled={(opcao) => opcao.isDisabled}
              placeholder="Selecione um Agente de IA"
            />
          )}
        />
        {contatoOrigemSelecionado?.twilio_ai_assistant_sid && contatoOrigemSelecionado?.id_agente_ia !== null && contatoOrigemSelecionado?.id_agente_ia !== undefined && (
          <Form.Text className="text-warning">
            O Agente de IA Dommus possui prioridade no fluxo de atendimento.
          </Form.Text>
        )}
      </Form.Group>
      )}

      <Form.Group className="form-configuracao-contato-auto__grupo">
        <Form.Label>Empreendimento</Form.Label>
        <Controller
          name="id_empreendimento"
          control={control}
          render={({ field }) => (
            <Select
              options={opcoesEmpreendimento}
              value={opcoesEmpreendimento.find((opt) => Number(opt.value) === Number(field.value)) || null}
              onChange={(opt) => field.onChange(opt ? opt.value : null)}
              placeholder="Selecione um Empreendimento"
              isClearable
            />
          )}
        />
      </Form.Group>

      <Form.Group className="form-configuracao-contato-auto__grupo">
        <Form.Label>Contato origem para criar atendimento</Form.Label>
        <Controller
          name="id_contato_auto_criar_atendimento"
          control={control}
          render={({ field }) => (
            <Select
              options={opcoesContatosOrigem}
              value={opcoesContatosOrigem.find((opt) => opt.value === field.value) || null}
              onChange={(opt) => field.onChange(opt ? opt.value : null)}
              placeholder="Selecione um contato"
              isClearable
            />
          )}
        />
      </Form.Group>

      <Form.Group className="form-configuracao-contato-auto__grupo">
        <Form.Label>Template para atendimento automático</Form.Label>
        <Controller
          name="id_template_criar_atendimento_auto"
          control={control}
          render={({ field }) => (
            <Select
              options={opcoesTemplates}
              value={opcoesTemplates.find((opt) => opt.value === field.value) || null}
              onChange={(opt) => field.onChange(opt ? opt.value : null)}
              placeholder="Selecione um template"
              isClearable
            />
          )}
        />
      </Form.Group>

      <Form.Group className="form-configuracao-contato-auto__grupo">
        <Form.Label>Template para resposta automática</Form.Label>
        <Controller
          name="id_template_resposta_auto_criar_atendimento"
          control={control}
          render={({ field }) => (
            <Select
              options={opcoesTemplates}
              value={opcoesTemplates.find((opt) => opt.value === field.value) || null}
              onChange={(opt) => field.onChange(opt ? opt.value : null)}
              placeholder="Selecione um template"
              isClearable
            />
          )}
        />
      </Form.Group>
      <Row>
        <Form.Group className="form-configuracao-contato-auto__grupo" as={Col}>
          <Form.Label>Account SID</Form.Label>
          <Form.Control 
            type="text" 
            {...register('account_sid_twilio', {required: true})}
          />
        </Form.Group>
        <Form.Group className="form-configuracao-contato-auto__grupo" as={Col}>
          <Form.Label>Criar atendimento WhatsApp automaticamente</Form.Label>
          <Controller
            name="criar_atendimento_whatsapp_auto"
            control={control}
            render={({ field }) => (
              <DommusSwitchControl
                labelAtivo={!!field.value ? "Sim" : "Não"}
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </Form.Group>
      </Row>


      <div className="form-configuracao-contato-rodape">
        <Button 
          variant="secondary" 
          type="button" 
          onClick={() => {closeModal()}}
        >
          Cancelar
        </Button>
        <Button variant="success" type="submit">
          Salvar
        </Button>
      </div>
    </Form>
  );
}
